import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Col, Row } from 'react-bootstrap';
import { useGetDocumentationsQuery } from '../../api/documentationApiSlice';
import Tabs from '../Tabs';
import TableOfContent from './documentation/TableOfContent';
import '../../stylesheets/highligth.scss'
import navigableContent from '../../services/sdk/navigableContent';
import useScrolledTableOfContent from './documentation/useScrolledTableOfContent';
import styled from 'styled-components';
import { useHistory, useLocation } from 'react-router-dom';
import { Loader } from '../components';

const DocBlock = styled.div`
  padding: 10px;
  box-shadow: 0 0 17px #ccc;
  background: #fff;
  overflow: auto;
  font-size: 14px;

  a {
    color: #787878;
    text-decoration: none;
    border-bottom: 1px dotted grey;

    &:hover {
      color: black;
      border-bottom: 1px solid black;
    }
  }

  h1 {
    margin-bottom: 22px;
    font-weight: 600;
    font-size: 2.3rem;
  }

  h2 {
    border-bottom: 1px solid #eaecef;
    margin-top: 32px;
    margin-bottom: 22px;
    padding-bottom: 5px;
    font-weight: 600;
    font-size: 1.9rem;
  }

  h3 {
    margin-bottom: 14px;
    font-weight: 600;
    font-size: 1.5rem;
    margin-top: 30px;
  }

  h4 {
    font-weight: 600;
    font-size: 14px;
    margin-top: 20px;
    margin-bottom: 15px;
  }

  .nav {
    display: inline-flex;
    height: 28px;
    &.nav-tabs {
      border-width: 0;
      width: 100%
    }

    .nav-item {
      font-size: 14px;
      line-height: 28px;
      text-transform: uppercase;
      line-height: 27px;
      padding: 0 7px;
      color: #999;

      &.active {
        border-bottom: 2px solid #ff513c;
        color: #333;
      }
    }
  }
`

const DocTab = ({as, icon, data}) => {
  const [formattedHtml, tableOfContent] = useMemo(() => navigableContent(data.html), [data.html])
  const pageRef = useRef()
  const {activeId, menuFixed} = useScrolledTableOfContent(pageRef)
  useEffect(() => {
    pageRef.current.querySelectorAll("a[href^='#']").forEach((link)=> {
      if( link.dataset.toggle != "tab" ) { //Skip no anchor link, must have a better way
        const anchorName = link.href.split('#')[1]
        const anchor = pageRef.current.querySelector(`a#${CSS.escape(anchorName)}`)
        link.onclick = (e)=>{
          e.stopPropagation()
          e.preventDefault()
          anchor.scrollIntoView()
        }
      }
    })
  }, [])


  return (
    <Row icon={icon} as={as}>
      <Col xs={10}>
        <DocBlock>
          { <div ref={pageRef} dangerouslySetInnerHTML={{ __html: formattedHtml }} /> }
        </DocBlock>
      </Col>
      <Col xs={2}>
        <TableOfContent
          list={tableOfContent}
          fixed={menuFixed}
          activeId={activeId}
          blockRef={pageRef}
        />
      </Col>
    </Row>
  )
}

const useSearchActiveTab = () => {
  const { search } = useLocation();
  const setSearchActiveTab = (activeTab)=>{
    const params = new URLSearchParams(search)
    if (activeTab) {
      params.set("active_tab", activeTab)
    } else {
      params.delete("active_tab")
    }
    return params
  }
  const getSearchActiveTab = ()=>{
    const params = new URLSearchParams(search)
    return params.get("active_tab")
  }
  return {setSearchActiveTab, getSearchActiveTab}
}

const DocumentationTab = () => {
  const {data = [], isSuccess, isLoading} = useGetDocumentationsQuery()
  const tabsChildren = data.map((d)=>{
    return (<DocTab data={d} icon={d.key} as={`sdkDoc.${d.key}`} />)
  })
  const history = useHistory()
  const {setSearchActiveTab, getSearchActiveTab} = useSearchActiveTab()
  const [activeTab, setActiveTab] = useState(`sdkDoc.${getSearchActiveTab()}`)

  useEffect(() => {
    history.push({search: setSearchActiveTab(activeTab.replace(/^sdkDoc\./, '')).toString()})
  }, [activeTab, history])

  useEffect(() => {
    if(isSuccess) {
      if ( getSearchActiveTab() ) {
        setActiveTab(`sdkDoc.${getSearchActiveTab()}`)
      } else {
        setActiveTab(data[0].key)
      }
    }
  }, [isSuccess])

  return ( <>
    <Loader success={!isLoading && isSuccess} error={!isLoading && !isSuccess}>
      <Tabs children={tabsChildren} onChange={(e)=>(setActiveTab(e))} active={activeTab}>
      </Tabs>
    </Loader>
  </> );
}

export default DocumentationTab;
